import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { EmployerUserContact } from '@restworld/utility-types';
import { RadioGroup } from '@restworld/ui-ds';
import { useVariableOptions } from '@restworld/utils-common';
import { useEmployerUserContactData } from '@restworld/feature-authentication';

import { useEmployerCommunicationPrefMutation } from './use-employer-user-information';
import { modalCommunicationPrefVisibleAtom } from '.';
import { ModalToggleWrapper } from './form-modal-wrapper';

export const EditUserCommunicationPrefModal = () => {
  const { t } = useTranslation('user-settings');

  const methods = useForm<EmployerUserContact>();
  const employerUserInformation = useEmployerUserContactData();
  const communicationVarKey = 'preferred_channel';
  const [modalCommunicationPrefVisible, modalCommunicationPrefVisibleToggler] = useAtom(
    modalCommunicationPrefVisibleAtom
  );
  const { mutate, isPending } = useEmployerCommunicationPrefMutation();
  const { fetchVariable, variableOptions } = useVariableOptions();

  const communicationChannelPreferences = React.useMemo(
    () => variableOptions[communicationVarKey] || [],
    [variableOptions]
  );

  const onSubmit: SubmitHandler<EmployerUserContact> = React.useCallback(
    (data) => {
      mutate({
        ...data,
        user_id: employerUserInformation?.user_id,
        preferred_communication_channel_var: communicationChannelPreferences.find(
          (v) => v.id === data.preferred_communication_channel_id
        ),
      });
    },
    [mutate, employerUserInformation?.user_id, communicationChannelPreferences]
  );

  React.useEffect(() => {
    fetchVariable([communicationVarKey]);
  }, [fetchVariable]);

  React.useEffect(() => {
    if (employerUserInformation?.preferred_communication_channel_id)
      methods.reset({
        preferred_communication_channel_id: employerUserInformation?.preferred_communication_channel_id,
      });
  }, [employerUserInformation, methods]);

  return (
    <ModalToggleWrapper
      modalVisible={modalCommunicationPrefVisible}
      onModalClose={() => modalCommunicationPrefVisibleToggler(false)}
      onSubmitHandler={methods.handleSubmit(onSubmit)}
      loading={isPending}
      title={t('how_do_you_prefer_to_be_contacted')}
      onCancelHandlerProps={{
        onClick: () => modalCommunicationPrefVisibleToggler(false),
        title: t('cancel'),
      }}
    >
      <Controller
        name="preferred_communication_channel_id"
        control={methods.control}
        rules={{
          required: t('communication_channel_is_required'),
        }}
        render={({ field }) => (
          <RadioGroup
            options={communicationChannelPreferences}
            id="preferred_communication_channel_id"
            orientation="horizontal"
            returnKey="id"
            onChange={field.onChange}
            value={field.value}
          />
        )}
      />
    </ModalToggleWrapper>
  );
};
